import type { GccDashboardData, GccEndStateRole, GccOwnership, GccPopulationSegment, GccRoleTag, GccSourcingTier } from '@shared/types'

/**
 * The Role Evolution Sankey of the exported dashboard (WI 2884): today's roles on the
 * left, and on the right either the end-state roles plus "Released by AI" (designs with
 * Step 4 source roles, WI 2883) or, without them, where today's work goes: Retained,
 * Captive GCC, MSP and Released by AI. Names, FTE, tags and skills only: no cost or
 * rate, so the Simple (client) export embeds it as it is.
 */

/** One of today's roles: a node on the left. */
interface GccRoleEvoToday {
  name: string
  fte: number
  /** Indexes into `roles` of the end-state roles this role becomes (for its card) */
  roles: number[]
}

/** A node on the right: an end-state role, an owner (fallback) or Released by AI. */
interface GccRoleEvoTarget {
  kind: 'role' | 'owner' | 'ai'
  name: string
  fte: number
  /** Index into `roles` (kind 'role') */
  role?: number
  /** End-state owner of the node's work (role and owner nodes), for its colour */
  owner?: GccOwnership
}

/** An end-state role as its card shows it. */
interface GccRoleEvoRole {
  name: string
  segment: GccPopulationSegment
  tag: GccRoleTag
  sourcingTier: GccSourcingTier
  headcountToday: number
  headcountEndState: number
  skillsOut: string[]
  skillsIn: string[]
  skillsNetNew: string[]
}

/**
 * One link: FTE from a role of today (`from`, into `today`) to a node (`to`, into
 * `targets`). `seg` and `tag` are those of the end-state role the FTE goes through,
 * and `fn` the Step 0 function it comes from, so the filters only have to sum links.
 */
interface GccRoleEvoFlow {
  from: number
  to: number
  fte: number
  seg: string
  tag: string
  fn: string
}

export interface GccRoleEvolution {
  /** 'sourceRoles': today's roles → end-state roles + AI. 'destination': the fallback, today's roles → owner + AI */
  mode: 'sourceRoles' | 'destination'
  today: GccRoleEvoToday[]
  targets: GccRoleEvoTarget[]
  roles: GccRoleEvoRole[]
  flows: GccRoleEvoFlow[]
  /** Left total; end-state headcount and AI-released FTE (right total = endState + ai) */
  totals: { today: number; endState: number; ai: number }
  /** Functions present on the links, in register order; empty when the design has none per role */
  functions: string[]
}

type Design = Pick<GccDashboardData, 'gccPopulation' | 'retainedPopulation' | 'valueCase'>

const OWNERS: GccOwnership[] = ['Retained', 'Captive-GCC', 'MSP']
const OWNER_NAMES: Record<GccOwnership, string> = { 'Retained': 'Retained', 'Captive-GCC': 'Captive GCC', 'MSP': 'MSP' }
const AI_NAME = 'Released by AI'

const cents = (n: number) => Math.round(n * 100)
/** Matching key of a role title, as in gcc-source-roles.ts: case and spacing don't matter. */
const roleKey = (name: string) => name.trim().replace(/\s+/g, ' ').toLowerCase()
const ownerOfTier = (t: GccSourcingTier): GccOwnership =>
  t === 'MSP' ? 'MSP' : t === 'GCC-Offshore' || t === 'GCC-Nearshore' ? 'Captive-GCC' : 'Retained'

/** Ordered map of today's roles: matching key → display name, in the order first seen. */
class TodayRoles {
  readonly keys: string[] = []
  private readonly names = new Map<string, string>()
  add(name: string): string {
    const key = roleKey(name)
    if (!this.names.has(key)) { this.names.set(key, String(name).trim()); this.keys.push(key) }
    return key
  }
  name(key: string): string { return this.names.get(key) as string }
}

/** A raw link before rounding: today's role key → target index, through one end-state role or line. */
interface RawFlow { key: string; to: number; fte: number; seg: string; tag: string; fn: string }

/**
 * Rounds the raw links to whole cents and merges equal ones. Each of today's roles'
 * links are rounded together, largest remainder first, so every left node adds up
 * exactly to its own total.
 */
function roundFlows(raw: RawFlow[], today: TodayRoles, rowCents: Map<string, number>): GccRoleEvoFlow[] {
  const merged = new Map<string, RawFlow>()
  for (const f of raw) {
    if (!(f.fte > 0)) continue
    const id = [f.key, f.to, f.seg, f.tag, f.fn].join('|')
    const m = merged.get(id)
    if (m) m.fte += f.fte
    else merged.set(id, { ...f })
  }
  const flows: GccRoleEvoFlow[] = []
  today.keys.forEach((key, from) => {
    const own = [...merged.values()].filter((f) => f.key === key)
    const exact = own.map((f) => f.fte * 100)
    const parts = exact.map(Math.floor)
    let left = (rowCents.get(key) as number) - parts.reduce((s, p) => s + p, 0)
    exact
      .map((e, i) => ({ i, rem: e - Math.floor(e) }))
      .sort((a, b) => b.rem - a.rem || a.i - b.i)
      .forEach(({ i }) => { if (left > 0) { parts[i] += 1; left -= 1 } })
    own.forEach((f, i) => {
      if (parts[i] > 0) flows.push({ from, to: f.to, fte: parts[i] / 100, seg: f.seg, tag: f.tag, fn: f.fn })
    })
  })
  return flows
}

/** Today's roles with FTE to draw, largest first (then by name). */
function sortedToday(today: TodayRoles, rowCents: Map<string, number>): TodayRoles {
  const drawn = new TodayRoles()
  today.keys
    .filter((k) => (rowCents.get(k) ?? 0) > 0)
    .sort((a, b) => (rowCents.get(b) as number) - (rowCents.get(a) as number) || today.name(a).localeCompare(today.name(b)))
    .forEach((k) => drawn.add(today.name(k)))
  return drawn
}

function cardRole(r: GccEndStateRole): GccRoleEvoRole {
  return {
    name: r.roleName,
    segment: r.segment,
    tag: r.tag,
    sourcingTier: r.sourcingTier,
    headcountToday: r.headcountToday,
    headcountEndState: r.headcountEndState,
    skillsOut: r.skillsOut ?? [],
    skillsIn: r.skillsIn ?? [],
    skillsNetNew: r.skillsNetNew ?? [],
  }
}

/** Splits `fte` of today's role `key` by its share of each function (one part, unnamed, when it has none). */
function byFunction(key: string, fte: number, shares: Map<string, Map<string, number>>): Array<[string, number]> {
  const own = shares.get(key)
  const total = own ? [...own.values()].reduce((s, c) => s + c, 0) : 0
  if (!own || total <= 0) return [['', fte]]
  return [...own].map(([fn, c]) => [fn, (fte * c) / total])
}

/**
 * Designs with source roles (WI 2883):
 *
 * 1. Today's roles and their FTE are the value ledger's (Σ baseline FTE by role title), so
 *    the left total equals Art of the Possible's "Today". v1 designs use the end-state
 *    roles' headcount today.
 * 2. Each of today's roles' source-role flows are rescaled to that FTE (a no-op when Step 0
 *    and the ledger agree). A role no entry names flows into the end-state roles with its
 *    name, by their headcount today, as in the reconciliation.
 * 3. Each end-state role keeps its end-state headcount out of what flows in, in proportion
 *    to each source; the rest of its inflow is Released by AI.
 * 4. A role whose inflow is below its end-state headcount takes the shortfall out of the
 *    AI-bound flow of every role, in proportion, so both totals stay exact.
 */
function fromSourceRoles(gcc: Design, roles: GccEndStateRole[]): GccRoleEvolution {
  const today = new TodayRoles()
  const todayCents = new Map<string, number>()
  const shares = new Map<string, Map<string, number>>()
  const functions: string[] = []
  const lines = gcc.valueCase?.tasks ?? []
  const addToday = (name: string, fte: number, fn: string) => {
    const key = today.add(name)
    todayCents.set(key, (todayCents.get(key) ?? 0) + cents(fte))
    if (!fn) return
    if (!functions.includes(fn)) functions.push(fn)
    const own = shares.get(key) ?? new Map<string, number>()
    own.set(fn, (own.get(fn) ?? 0) + cents(fte))
    shares.set(key, own)
  }
  if (lines.length) lines.forEach((l) => addToday(l.roleTitle, l.baselineFte, l.functionScope))
  else roles.forEach((r) => addToday(r.roleName, r.headcountToday, ''))

  // Step 2: source flows per (today's role, end-state role), rescaled to today's FTE
  const flow = new Map<string, number[]>()
  for (const key of today.keys) {
    const weights = roles.map((r) => (r.sourceRoles ?? []).filter((s) => roleKey(s.role) === key).reduce((s, e) => s + (Number(e.fte) > 0 ? Number(e.fte) : 0), 0))
    if (!weights.some((w) => w > 0)) roles.forEach((r, i) => { if (roleKey(r.roleName) === key && r.headcountToday > 0) weights[i] = r.headcountToday })
    const total = weights.reduce((s, w) => s + w, 0)
    if (total > 0) flow.set(key, weights.map((w) => (w / total) * ((todayCents.get(key) as number) / 100)))
  }

  // Step 3: what each end-state role keeps and releases
  const inflow = roles.map((_, i) => [...flow.values()].reduce((s, row) => s + row[i], 0))
  const endFte = roles.map((r) => Math.max(0, r.headcountEndState))
  const keep = new Map<string, number[]>()
  const release = new Map<string, number[]>()
  for (const [key, row] of flow) {
    keep.set(key, row.map((f, i) => (inflow[i] > 0 ? f * Math.min(1, endFte[i] / inflow[i]) : 0)))
    release.set(key, row.map((f, i) => f - (keep.get(key) as number[])[i]))
  }

  // Step 4: shortfalls come out of the AI-bound flow, in proportion
  const deficit = roles.map((_, i) => Math.max(0, endFte[i] - inflow[i]))
  const totalDeficit = deficit.reduce((s, d) => s + d, 0)
  const totalRelease = [...release.values()].reduce((s, row) => s + row.reduce((a, b) => a + b, 0), 0)
  const cover = new Map<string, number[]>()
  if (totalDeficit > 0 && totalRelease > 0) {
    const factor = Math.min(totalDeficit, totalRelease) / totalRelease
    for (const [key, row] of release) {
      const moved = row.reduce((s, r) => s + r * factor, 0)
      release.set(key, row.map((r) => r * (1 - factor)))
      cover.set(key, deficit.map((d) => (moved * d) / totalDeficit))
    }
  }

  // Nodes on the right: end-state roles with a headcount, by population then size; AI last
  const order = roles.map((_, i) => i).filter((i) => endFte[i] > 0)
    .sort((a, b) => (roles[a].segment === roles[b].segment ? 0 : roles[a].segment === 'GCC-eligible' ? -1 : 1) || endFte[b] - endFte[a] || roles[a].roleName.localeCompare(roles[b].roleName))
  const targets: GccRoleEvoTarget[] = order.map((i) => ({ kind: 'role', name: roles[i].roleName, fte: cents(endFte[i]) / 100, role: i, owner: ownerOfTier(roles[i].sourcingTier) }))
  const targetOf = new Map(order.map((i, t) => [i, t]))
  const leftCents = [...flow.keys()].reduce((s, k) => s + (todayCents.get(k) as number), 0)
  const endCents = order.reduce((s, i) => s + cents(endFte[i]), 0)
  const aiCents = Math.max(0, leftCents - endCents)
  const aiIdx = targets.length
  targets.push({ kind: 'ai', name: AI_NAME, fte: aiCents / 100 })

  const raw: RawFlow[] = []
  const push = (key: string, to: number, fte: number, r: GccEndStateRole) => {
    for (const [fn, v] of byFunction(key, fte, shares)) raw.push({ key, to, fte: v, seg: r.segment, tag: r.tag, fn })
  }
  // A role with no end-state headcount keeps nothing, so all that flows into it is released
  const fed = new Map([...flow.keys()].map((k) => [k, [] as number[]]))
  for (const [key, row] of flow) {
    const covered = cover.get(key)
    roles.forEach((r, i) => {
      const t = targetOf.get(i)
      if (t !== undefined) push(key, t, (keep.get(key) as number[])[i] + (covered ? covered[i] : 0), r)
      push(key, aiIdx, (release.get(key) as number[])[i], r)
      if (row[i] > 0 || (covered && covered[i] > 0)) fed.get(key)?.push(i)
    })
  }
  const rowCents = new Map([...flow.keys()].map((k) => [k, todayCents.get(k) as number]))
  const drawn = sortedToday(today, rowCents)
  return {
    mode: 'sourceRoles',
    today: drawn.keys.map((k) => ({ name: drawn.name(k), fte: (rowCents.get(k) as number) / 100, roles: fed.get(k) as number[] })),
    targets,
    roles: roles.map(cardRole),
    flows: roundFlows(raw, drawn, rowCents),
    totals: { today: leftCents / 100, endState: endCents / 100, ai: aiCents / 100 },
    functions,
  }
}

/**
 * The fallback for designs without source roles: today's roles → where their work goes.
 * v2 designs take each value ledger line's residual FTE to its owner and its AI-released
 * FTE to Released by AI, so both totals are the ledger's. v1 designs (no ledger) use each
 * end-state role: its end-state headcount to the owner of its tier, today less end state
 * to Released by AI.
 */
function fromDestinations(gcc: Design, roles: GccEndStateRole[]): GccRoleEvolution {
  const today = new TodayRoles()
  const lines = gcc.valueCase?.tasks ?? []
  const functions: string[] = []
  const raw: Array<Omit<RawFlow, 'to'> & { dest: GccOwnership | 'AI' }> = []
  const tagOf = (role: string, tier: GccSourcingTier, seg: string) =>
    roles.find((r) => roleKey(r.roleName) === roleKey(role) && r.sourcingTier === tier && r.segment === seg)?.tag ?? ''
  if (lines.length) {
    for (const l of lines) {
      const key = today.add(l.roleTitle)
      if (l.functionScope && !functions.includes(l.functionScope)) functions.push(l.functionScope)
      const tag = tagOf(l.roleTitle, l.endTier, l.segment)
      raw.push({ key, dest: l.ownership, fte: l.residualFte, seg: l.segment, tag, fn: l.functionScope })
      raw.push({ key, dest: 'AI', fte: l.aiReleasedFte, seg: l.segment, tag, fn: l.functionScope })
    }
  } else {
    for (const r of roles) {
      const key = today.add(r.roleName)
      const end = Math.max(0, r.headcountEndState)
      raw.push({ key, dest: ownerOfTier(r.sourcingTier), fte: end, seg: r.segment, tag: r.tag, fn: '' })
      raw.push({ key, dest: 'AI', fte: Math.max(0, r.headcountToday - end), seg: r.segment, tag: r.tag, fn: '' })
    }
  }
  const destCents = (d: GccOwnership | 'AI') => raw.filter((f) => f.dest === d).reduce((s, f) => s + cents(f.fte), 0)
  const dests = [...OWNERS, 'AI' as const].filter((d) => destCents(d) > 0)
  const targets: GccRoleEvoTarget[] = dests.map((d) => (d === 'AI'
    ? { kind: 'ai', name: AI_NAME, fte: destCents(d) / 100 }
    : { kind: 'owner', name: OWNER_NAMES[d], fte: destCents(d) / 100, owner: d }))
  const rowCents = new Map(today.keys.map((k) => [k, raw.filter((f) => f.key === k).reduce((s, f) => s + cents(f.fte), 0)]))
  const drawn = sortedToday(today, rowCents)
  const aiCents = destCents('AI')
  const leftCents = drawn.keys.reduce((s, k) => s + (rowCents.get(k) as number), 0)
  return {
    mode: 'destination',
    today: drawn.keys.map((k) => ({ name: drawn.name(k), fte: (rowCents.get(k) as number) / 100, roles: roles.map((r, i) => (roleKey(r.roleName) === k ? i : -1)).filter((i) => i >= 0) })),
    targets,
    roles: roles.map(cardRole),
    flows: roundFlows(raw.map((f) => ({ ...f, to: dests.indexOf(f.dest) })).filter((f) => f.to >= 0), drawn, rowCents),
    totals: { today: leftCents / 100, endState: (leftCents - aiCents) / 100, ai: aiCents / 100 },
    functions,
  }
}

/**
 * The Role Evolution Sankey of a design (WI 2884), or null when it has no end-state roles.
 * Uses the source roles when any end-state role has them, else the destination fallback.
 * S0b exclusions don't apply: the left total is the whole baseline, as in Art of the Possible.
 */
export function gccRoleEvolution(gcc: Design): GccRoleEvolution | null {
  const roles = [...(gcc.gccPopulation?.endStateRoles ?? []), ...(gcc.retainedPopulation?.endStateRoles ?? [])]
  if (!roles.length) return null
  return roles.some((r) => Array.isArray(r.sourceRoles)) ? fromSourceRoles(gcc, roles) : fromDestinations(gcc, roles)
}
